/* eslint-disable max-lines-per-function */
/* TODO(Smári): Remove eslint-disable line above and refactor this file! */
import React, { useEffect, useState } from 'react';

import './RTCCaptionControl.css';
import { Alert, Form, Popover, Radio, RadioChangeEvent, Space, Spin, Switch, Tooltip, Typography } from 'antd';
import { ModelInfo } from '../types';
import { CAPTION_RTMP_API_URL } from '../config';
import { AuthContextProps, withAuth } from '../LocalAuthWrapper';
import { InfoCircleOutlined } from '@ant-design/icons';
import Paragraph from 'antd/es/typography/Paragraph';

const { Text } = Typography;

type RTCCaptionControlProps = {
  userId: string;
  streamKeyPublicId: string;
} & AuthContextProps;

type RTCCaptionControlState = {
  captioningOn?: boolean;
  currentlySelectedModel?: ModelInfo;
  availableModels: ModelInfo[];
  loading: boolean;
};

const initialState: RTCCaptionControlState = {
  captioningOn: undefined,
  currentlySelectedModel: undefined,
  availableModels: [],
  loading: false,
};

function RTCCaptionControl(props: RTCCaptionControlProps) {
  const [state, setState] = useState<RTCCaptionControlState>(initialState);

  const getAvailableModels = async () => {
    const response = await fetch(`${CAPTION_RTMP_API_URL}/models`);
    if (!response.ok) {
      const data: any = await response.json();
      console.error(
        data && data.error && data.error.log ? data.error.log : 'Failed to fetch available models. Reason unspecified.',
      );
      return;
    }

    const json = await response.json();
    const modelList: ModelInfo[] = [];
    json.forEach((item: { language_code: string; name: string; description: string }) => {
      modelList.push({
        languageCode: item.language_code,
        name: item.name,
        description: item.description,
      } as ModelInfo);
    });
    setState((state: RTCCaptionControlState) => ({ ...state, availableModels: modelList }));
  };

  const getCurrentlySelectedModel = async (flags: { initialLoad: boolean; showLoading: boolean }) => {
    if (!props.streamKeyPublicId) {
      return;
    }

    flags.showLoading && setState((state: RTCCaptionControlState) => ({ ...state, loading: true }));
    const response = await fetch(
      `${CAPTION_RTMP_API_URL}/stream_keys/${props.userId}/${props.streamKeyPublicId}?include_broadcasts=false`,
      {
        headers: { authorization: `Bearer ${props.authCtx.accessToken}` },
      },
    );
    if (!response.ok) {
      const data: any = await response.json();
      console.error(
        data && data.error && data.error.log
          ? data.error.log
          : 'Failed to fetch currently selected model. Reason unspecified.',
      );
      return;
    }
    const data: any = await response.json();
    setState((state: RTCCaptionControlState) => ({
      ...state,
      currentlySelectedModel: data.model
        ? {
            name: data.model.name,
            languageCode: data.model.language_code,
            description: data.model.description,
          }
        : undefined,
      captioningOn: data.model !== null,
      ...(flags.showLoading && { loading: false }),
    }));
  };

  const setModelConfig = async (languageCode: string) => {
    if (!props.streamKeyPublicId) {
      console.warn('Stream key public id not supplied for caption control.');
      return;
    }

    setState((state: RTCCaptionControlState) => ({ ...state, loading: true }));

    const response = await fetch(
      `${CAPTION_RTMP_API_URL}/switch_lang/${props.userId}/${props.streamKeyPublicId}/${languageCode}`,
      {
        method: 'POST',
        headers: { authorization: `Bearer ${props.authCtx.accessToken}` },
      },
    );
    const data: any = await response.json();

    if (!response.ok) {
      console.error(
        data && data.error && data.error.log
          ? data.error.log
          : 'Stream key model switching failed. Reason unspecified.',
      );
      setState((state: RTCCaptionControlState) => ({ ...state, loading: false }));
      return;
    }

    const currentModel: ModelInfo | undefined =
      Object.keys(data).length !== 0
        ? ({
            name: data.name,
            languageCode: data.language_code,
            description: data.description,
          } as ModelInfo)
        : undefined;
    setState((state: RTCCaptionControlState) => ({
      ...state,
      currentlySelectedModel: currentModel,
      captioningOn: currentModel !== undefined,
      loading: false,
    }));
  };

  const changeModel = ({ target: { value } }: RadioChangeEvent) => {
    setModelConfig(value);
  };

  useEffect(() => {
    const init = async () => {
      setState((state: RTCCaptionControlState) => ({ ...state, loading: true }));
      state.availableModels.length === 0 && (await getAvailableModels());
      await getCurrentlySelectedModel({ initialLoad: !state.currentlySelectedModel, showLoading: false });
      setState((state: RTCCaptionControlState) => ({ ...state, loading: false }));
    };
    init();
  }, [state.captioningOn, props.streamKeyPublicId]);

  return state.availableModels.length > 0 ? (
    <Form.Item label='Stillingar streymislykils' colon={false}>
      <div id='caption-control'>
        <Space>
          <Space direction='vertical' align='start' style={{ marginLeft: '10px' }}>
            <Space>
              <Text strong>Textun</Text>
              <div id='lang-info-icon'>
                <Popover
                  title='Textunarstjórnun'
                  content={
                    <Space size='small' direction='vertical' style={{ width: '400px' }}>
                      <Paragraph>
                        Hér má breyta tungumáli textunar ef til þess kemur að fleira en eitt tungumál er talað í
                        útsendingu. Ef ekki er til staðar stuðningur við það tungumál sem er talað þá er hægt að slökkva
                        á textuninni.
                      </Paragraph>
                      <Paragraph>
                        <b>
                          Athugið að stillingar streymislykils eru óháðar útsendingum. Ef stillingum er breytt hér þá
                          hefur það áhrif á aðrar útsendingar sem nota lykilinn.
                        </b>
                      </Paragraph>
                    </Space>
                  }
                >
                  <InfoCircleOutlined />
                </Popover>
              </div>
            </Space>
            <Space
              style={{
                marginBottom: '10px',
              }}
            >
              <Tooltip title={'Slökkva eða kveikja á rauntímatextun'}>
                <Switch
                  checkedChildren='Kveikt'
                  unCheckedChildren='Slökkt'
                  checked={state.captioningOn}
                  onClick={() => setModelConfig(state.captioningOn ? 'none' : 'is')}
                />
              </Tooltip>
              <div className={state.loading ? 'spinner-visible' : 'spinner-invisible'}>
                <Spin size='small' style={{ marginRight: '8px' }} />
              </div>
            </Space>
          </Space>
          <Space>
            <Radio.Group
              onChange={changeModel}
              disabled={!state.captioningOn}
              value={state.currentlySelectedModel?.languageCode}
              buttonStyle='solid'
              optionType='button'
            >
              <Space direction='vertical'>
                {state.availableModels.map((model: ModelInfo, index) => (
                  <React.Fragment key={index}>
                    <Tooltip
                      title={
                        state.availableModels.length === 1 && state.captioningOn
                          ? 'Um stundarsakir stendur aðeins eitt tungumál til boða'
                          : ''
                      }
                    >
                      <Radio.Button
                        key={index}
                        value={model.languageCode}
                        checked={model.languageCode === state.currentlySelectedModel?.languageCode}
                        style={{ width: '100%' }}
                      >
                        {model.name}
                      </Radio.Button>
                    </Tooltip>
                  </React.Fragment>
                ))}
              </Space>
            </Radio.Group>
            <Alert
              style={{ width: '400px', height: '160px' }}
              message={
                state.captioningOn ? (
                  <Paragraph>
                    {'Efni streymt með þessum lykli er nú textað á þessu tungumáli: '}
                    <b>{state.currentlySelectedModel?.name}</b>
                  </Paragraph>
                ) : (
                  <b>Slökkt er á rauntímatextun</b>
                )
              }
              description={
                state.captioningOn
                  ? state.currentlySelectedModel?.description
                  : 'Efni streymt með þessum lykli er ótextað'
              }
              type='info'
            />
          </Space>
        </Space>
      </div>
    </Form.Item>
  ) : (
    <></>
  );
}

export default withAuth(RTCCaptionControl);
